// re-push trigger
import React, { useState, useEffect } from 'react';
import BackButton from '../components/BackButton';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton
} from '@mui/material';
import axios from 'axios';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const isValidIranianNationalCode = (code) => {
  if (!code) return false;
  const cleaned = String(code).trim();
  if (!/^\d{10}$/.test(cleaned)) return false;
  // reject same digits like 0000000000, 1111111111, ...
  if (/^(\d)\1{9}$/.test(cleaned)) return false;
  const digits = cleaned.split('').map(Number);
  const check = digits[9];
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += digits[i] * (10 - i);
  }
  const remainder = sum % 11;
  return (remainder < 2 && check === remainder) || (remainder >= 2 && check === 11 - remainder);
};

const isValidIranMobile = (phone) => {
  if (!phone) return false;
  const raw = String(phone).trim();
  // Accept formats: 09xxxxxxxxx or +989xxxxxxxx or 989xxxxxxxx
  // Normalize: remove spaces, dashes
  const cleaned = raw.replace(/[\s\-()]/g, '');
  // patterns:
  if (/^09\d{9}$/.test(cleaned)) return true;
  if (/^\+989\d{9}$/.test(cleaned)) return true;
  if (/^989\d{9}$/.test(cleaned)) return true;
  return false;
};

const AddStudentPage = () => {
  const [name, setName] = useState('');
  const [code, setCode] = useState(''); // کد ملی یا شناسه اتباع
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // parent
  const [parentName, setParentName] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentEmail, setParentEmail] = useState('');

  // grade
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('');

  // ui
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isForeign, setIsForeign] = useState(false);

  const token = localStorage.getItem('teacherToken');
  const role = localStorage.getItem('teacherRole'); // "Teacher" or "Admin"

  // اگر backend الان فقط Admin می‌تواند ثبت کند این را true نگه دار
  const BACKEND_IS_ADMIN_ONLY = true;

  useEffect(() => {
    const fetchGrades = async () => {
      setError('');
      try {
        const res = await axios.get('https://education-backend.liara.run/api/grade', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGrades(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('❌ خطا در دریافت پایه‌ها:', err);
        setError('خطا در دریافت لیست پایه‌ها');
      }
    };
    fetchGrades();
  }, [token]);

  // لحظه‌ای اعتبارسنجی‌ها برای نمایش پیام خطا
  const [codeError, setCodeError] = useState('');
  const [parentPhoneError, setParentPhoneError] = useState('');
  const [nameError, setNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [selectedGradeError, setSelectedGradeError] = useState('');

  useEffect(() => {
    // name
    setNameError(name.trim() ? '' : 'نام دانش‌آموز الزامی است.');
  }, [name]);

  useEffect(() => {
    // code: اگر اتباع نیست => باید کد ملی معتبر باشد
    if (isForeign) {
      setCodeError(code.trim() ? '' : 'شناسه اتباع یا شماره پاسپورت الزامی است.');
    } else {
      if (!code.trim()) {
        setCodeError('کد ملی الزامی است.');
      } else if (!isValidIranianNationalCode(code.trim())) {
        setCodeError('کد ملی نامعتبر است.');
      } else {
        setCodeError('');
      }
    }
  }, [code, isForeign]);

  useEffect(() => {
    // parent phone
    if (!parentPhone.trim()) {
      setParentPhoneError('');
    } else if (!isValidIranMobile(parentPhone.trim())) {
      setParentPhoneError('شماره موبایل نامعتبر است. مثال: 0912xxxxxxx یا +98912xxxxxxx');
    } else {
      setParentPhoneError('');
    }
  }, [parentPhone]);

  useEffect(() => {
    // password
    if (!password) {
      setPasswordError('رمز عبور الزامی است.');
    } else if (password.length < 6) {
      setPasswordError('رمز عبور باید حداقل 6 کاراکتر باشد.');
    } else {
      setPasswordError('');
    }
  }, [password]);

  useEffect(() => {
    setSelectedGradeError(selectedGrade ? '' : 'پایه انتخاب نشده است.');
  }, [selectedGrade]);

  const canSubmit = () => {
    // همه خطاها باید خالی باشند و فیلدهای الزامی باید پر باشند
    if (BACKEND_IS_ADMIN_ONLY && role !== 'Admin') return false;
    if (!name.trim()) return false;
    if (!selectedGrade) return false;
    if (!password) return false;
    if (!code.trim()) return false;
    if (nameError || codeError || parentPhoneError || passwordError || selectedGradeError) return false;
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // final validation guard
    if (!canSubmit()) {
      setError('لطفاً خطاها را اصلاح کنید تا امکان ثبت وجود داشته باشد.');
      return;
    }

    try {
      const payload = {
        name: name.trim(),
        code: code.trim(),
        password,
        gradeId: parseInt(selectedGrade, 10),
        parent: {
          name: parentName.trim() || '',
          phone: parentPhone.trim() || '',
          email: parentEmail.trim() || ''
        },
        isForeign: !!isForeign
      };

      if (BACKEND_IS_ADMIN_ONLY && role !== 'Admin') {
        setError('فقط کاربر با نقش Admin اجازهٔ ثبت دانش‌آموز را دارد. لطفاً با حساب مدیر وارد شوید.');
        return;
      }

      const res = await axios.post(
        'https://education-backend.liara.run/api/student/register',
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(res.data?.message || 'دانش‌آموز با موفقیت ثبت شد');

      // reset form
      setName(''); setCode(''); setPassword('');
      setParentName(''); setParentPhone(''); setParentEmail('');
      setSelectedGrade('');
      setIsForeign(false);
    } catch (err) {
      const errorObj = err.response?.data || { message: err.message };
      console.error('[REGISTER] Error:', errorObj);
      setError(errorObj?.error || errorObj?.message || 'خطای ثبت رخ داد');
    }
  };

  return (
    <Box sx={{ padding: '2rem', maxWidth: '900px', margin: 'auto' }} dir="rtl">
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
        <BackButton toFallback="/dashboard" />
      </header>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>ثبت دانش‌آموز جدید 👨‍🎓</Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <TextField
            label="نام دانش‌آموز"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            error={!!nameError}
            helperText={nameError || ''}
          />

          <FormControlLabel
            control={<Checkbox checked={isForeign} onChange={(e) => setIsForeign(e.target.checked)} />}
            label="اتباع (غیرايرانی / پاسپورت)"
            sx={{ mb: 2 }}
          />

          <TextField
            label={isForeign ? "شناسه اتباع یا شماره پاسپورت" : "کد ملی (10 رقمی)"}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            error={!!codeError}
            helperText={codeError || (isForeign ? 'در صورت اتباع، شماره پاسپورت یا شناسه اتباع را وارد کنید.' : '')}
          />

          <TextField
            label="رمز عبور"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            error={!!passwordError}
            helperText={passwordError || 'حداقل 6 کاراکتر'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(s => !s)} edge="end" aria-label="toggle password visibility">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            select
            label="انتخاب پایه"
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            SelectProps={{ native: true }}
            error={!!selectedGradeError}
            helperText={selectedGradeError || ''}
          >
            <option value="" />
            {grades.map((g) => (
              <option key={g.id ?? g.Id} value={g.id ?? g.Id}>
                {(g.level ?? g.Level) + ' - ' + (g.stream ?? g.Stream)}
              </option>
            ))}
          </TextField>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>اطلاعات والدین:</Typography>
          <TextField label="نام والد" value={parentName} onChange={(e) => setParentName(e.target.value)} fullWidth sx={{ mb: 1 }} />
          <TextField
            label="شماره تماس والد"
            value={parentPhone}
            onChange={(e) => setParentPhone(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            error={!!parentPhoneError}
            helperText={parentPhoneError || 'اختیاری اما در صورت وارد کردن باید معتبر باشد'}
          />
          <TextField label="ایمیل والد" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} fullWidth sx={{ mb: 2 }} />

          {BACKEND_IS_ADMIN_ONLY && role !== 'Admin' ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              ثبت دانش‌آموز در حال حاضر تنها توسط «مدیر» (Admin) مجاز است. برای ثبت لطفاً با حساب مدیر وارد شوید.
            </Alert>
          ) : null}

          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={!canSubmit()}
          >
            ثبت دانش‌آموز
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddStudentPage;